(() => {
  if (customElements.get('social-video-showcase')) return;

  class SocialVideoShowcase extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.modal = this.querySelector('[data-fkk-video-modal]');
      this.modalContent = this.modal?.querySelector('[data-fkk-modal-content]');
      this.track = this.querySelector('.social-video-showcase__track');
      this.previousButton = this.querySelector('[data-fkk-slider-button="previous"]');
      this.nextButton = this.querySelector('[data-fkk-slider-button="next"]');
      this.lastTrigger = null;

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleModalClick = this.handleModalClick.bind(this);
      this.boundHandleModalClose = this.handleModalClose.bind(this);
      this.boundUpdateSliderControls = this.updateSliderControls.bind(this);

      this.addEventListener('click', this.boundHandleClick);
      this.modal?.addEventListener('click', this.boundHandleModalClick);
      this.modal?.addEventListener('close', this.boundHandleModalClose);
      this.track?.addEventListener('scroll', this.boundUpdateSliderControls, { passive: true });

      if (this.track && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.boundUpdateSliderControls);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.boundUpdateSliderControls);
      }

      requestAnimationFrame(this.boundUpdateSliderControls);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundHandleClick);
      this.modal?.removeEventListener('click', this.boundHandleModalClick);
      this.modal?.removeEventListener('close', this.boundHandleModalClose);
      this.track?.removeEventListener('scroll', this.boundUpdateSliderControls);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.boundUpdateSliderControls);
    }

    handleClick(event) {
      const sliderButton = event.target.closest('[data-fkk-slider-button]');
      if (sliderButton && this.contains(sliderButton)) {
        this.scrollSlider(sliderButton.dataset.fkkSliderButton === 'next' ? 1 : -1);
        return;
      }

      const trigger = event.target.closest('[data-fkk-video-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const template = trigger.parentElement.querySelector('[data-fkk-video-template]');
      if (!template || !this.modal || !this.modalContent) return;

      this.openModal(trigger, template);
    }

    scrollSlider(direction) {
      if (!this.track) return;

      const firstItem = this.track.querySelector('.social-video-showcase__item');
      if (!firstItem) return;

      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const distance = (firstItem.getBoundingClientRect().width + gap) * direction;

      this.track.scrollBy({ left: distance, behavior: 'smooth' });
    }

    updateSliderControls() {
      if (!this.track || !this.previousButton || !this.nextButton) return;

      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const canScroll = maximumScroll > 2;
      const currentScroll = Math.abs(this.track.scrollLeft);
      const isAtStart = currentScroll <= 2;
      const isAtEnd = currentScroll >= maximumScroll - 2;

      this.previousButton.hidden = !canScroll || isAtStart;
      this.nextButton.hidden = !canScroll || isAtEnd;
      this.previousButton.disabled = !canScroll || isAtStart;
      this.nextButton.disabled = !canScroll || isAtEnd;
    }

    handleModalClick(event) {
      if (event.target === this.modal || event.target.closest('[data-fkk-modal-close]')) {
        this.closeModal();
      }
    }

    openModal(trigger, template) {
      this.pauseNativeVideos();
      this.lastTrigger = trigger;

      const modalFragment = template.content.cloneNode(true);
      const panel = modalFragment.querySelector('[data-fkk-modal-panel]');
      const player = modalFragment.querySelector('[data-fkk-video-player]');
      if (!panel || !player) return;

      modalFragment.querySelectorAll('[data-src]').forEach((element) => {
        element.src = element.dataset.src;
        element.removeAttribute('data-src');
      });

      this.modalContent.replaceChildren(modalFragment);
      this.modal.setAttribute('aria-labelledby', panel.dataset.modalTitleId);
      document.body.classList.add('fkk-video-modal-open');

      if (typeof this.modal.showModal === 'function') {
        this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }

      if (player instanceof HTMLVideoElement) {
        player.muted = true;
        player.load();
        const playback = player.play();

        if (playback) {
          playback.catch((error) => {
            console.warn('Video playback did not start automatically.', error);
            player.focus();
          });
        }
      }
    }

    closeModal() {
      if (!this.modal) return;

      if (typeof this.modal.close === 'function' && this.modal.open) {
        this.modal.close();
        this.handleModalClose();
      } else {
        this.modal.removeAttribute('open');
        this.handleModalClose();
      }
    }

    handleModalClose() {
      this.pauseNativeVideos();
      this.modalContent?.replaceChildren();
      this.modal?.removeAttribute('aria-labelledby');
      document.body.classList.remove('fkk-video-modal-open');

      if (this.lastTrigger?.isConnected) this.lastTrigger.focus();
      this.lastTrigger = null;
    }

    pauseNativeVideos() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }
  }

  customElements.define('social-video-showcase', SocialVideoShowcase);
})();
